import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Content,
	ContentVariants,
	Flex,
	FlexItem,
	Skeleton,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { ArrowRightIcon, GripVerticalIcon } from "@patternfly/react-icons";
import type { FC, ReactNode } from "react";
import type { DragHandleProps } from "../../../constants/CardsStructure";

export interface DashboardCardLink {
	label: string;
	href: string;
	openNewTab?: boolean;
	variant?: "link" | "primary";
}

export interface DashboardCardProps {
	icon?: string;
	title: ReactNode | string;
	description?: string;
	subtext?: string;
	cardBody?: ReactNode;
	footerLinks?: DashboardCardLink[];
	primaryAction?: DashboardCardLink;
	isLoading?: boolean;
	dragHandleProps?: DragHandleProps | undefined;
	cardFooterNode?: ReactNode;
}

export const DashboardCard: FC<DashboardCardProps> = ({
	icon,
	title,
	description,
	subtext,
	cardBody,
	footerLinks,
	primaryAction,
	isLoading,
	dragHandleProps,
	cardFooterNode,
}) => {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<Flex alignItems={{ default: "alignItemsCenter" }} gap={{ default: "gapSm" }}>
						<FlexItem>
							<Skeleton shape="circle" width="24px" height="24px" />
						</FlexItem>
						<FlexItem>
							<Skeleton width="150px" fontSize="lg" />
						</FlexItem>
					</Flex>
				</CardHeader>
				<CardBody>
					<Stack hasGutter>
						<StackItem>
							<Skeleton fontSize="md" />
						</StackItem>
						<StackItem>
							<Skeleton fontSize="md" />
						</StackItem>
					</Stack>
				</CardBody>
				<CardFooter>
					<Skeleton width="120px" fontSize="md" />
				</CardFooter>
			</Card>
		);
	}

	const hasFooter = footerLinks?.length || cardFooterNode;

	return (
		<Card isFullHeight className="dashboard-widget">
			<CardHeader
				{...(dragHandleProps && {
					actions: {
						actions: (
							<Button
								icon={<GripVerticalIcon />}
								variant="plain"
								aria-label="Drag to reorder"
								{...dragHandleProps.attributes}
								{...dragHandleProps.listeners}
							/>
						),
						hasNoOffset: true,
					},
				})}
			>
				<Flex alignItems={{ default: "alignItemsCenter" }} gap={{ default: "gapSm" }}>
					{icon && (
						<FlexItem>
							<img src={icon} alt="" aria-hidden style={{ height: "24px", width: "24px" }} />
						</FlexItem>
					)}
					<FlexItem>
						<Content>
							{typeof title === "string" ? (
								<Content component={ContentVariants.h3} style={{ margin: 0 }}>
									{title}
								</Content>
							) : (
								title
							)}
						</Content>
					</FlexItem>
				</Flex>
			</CardHeader>

			{(description || subtext || cardBody || primaryAction) && (
				<CardBody>
					<Stack hasGutter>
						{(description || subtext) && (
							<StackItem>
								<Content>
									<Content component={ContentVariants.p}>
										{description}
										{subtext && (
											<Content component={ContentVariants.small} style={{ color: "#1f1f1f", fontStyle: "italic" }}>
												{subtext}
											</Content>
										)}
									</Content>
								</Content>
							</StackItem>
						)}
						{cardBody && <StackItem>{cardBody}</StackItem>}
						{primaryAction && (
							<StackItem>
								<Button
									variant="primary"
									component="a"
									href={primaryAction.href}
									{...(primaryAction.openNewTab ? { target: "_blank" } : {})}
								>
									{primaryAction.label}
								</Button>
							</StackItem>
						)}
					</Stack>
				</CardBody>
			)}

			{hasFooter && (
				<CardFooter>
					{footerLinks && (
						<Flex gap={{ default: "gapLg" }}>
							{footerLinks.map((link, i) => (
								<FlexItem key={i}>
									<Button
										variant="link"
										component="a"
										href={link.href}
										{...(link.openNewTab ? { target: "_blank" } : {})}
										icon={<ArrowRightIcon />}
										iconPosition="end"
										isInline
									>
										{link.label}
									</Button>
								</FlexItem>
							))}
						</Flex>
					)}
					{cardFooterNode}
				</CardFooter>
			)}
		</Card>
	);
};
